/**
 * TagChipInput — the shared chip-entry control (T17 behaviour):
 * comma or Enter commits the typed text as a chip, Backspace on an empty
 * input removes the last chip, blur commits any pending text, duplicates
 * are ignored case-insensitively.
 *
 * Extracted from TaskForm/ProjectForm, which had drifted into two
 * identical inline implementations. The draft string stays parent-owned
 * so submit paths can flush text the user typed but never committed
 * (see mergePendingTag).
 */

import { Icon } from "./Icon";

export type TagChipInputProps = {
  id: string;
  names: string[];
  onNamesChange: (names: string[]) => void;
  draft: string;
  onDraftChange: (draft: string) => void;
  /** Chip dot color by tag name; omit to render plain chips. */
  colorFor?: (name: string) => string;
};

/** Returns `names` with `draft` appended, unless blank or already present. */
export function mergePendingTag(names: string[], draft: string): string[] {
  const pending = draft.trim();
  if (!pending) return names;
  if (names.some((n) => n.toLowerCase() === pending.toLowerCase())) {
    return names;
  }
  return [...names, pending];
}

export function TagChipInput({
  id,
  names,
  onNamesChange,
  draft,
  onDraftChange,
  colorFor,
}: TagChipInputProps) {
  const commit = (raw: string) => {
    const merged = mergePendingTag(names, raw);
    if (merged !== names) onNamesChange(merged);
    onDraftChange("");
  };
  return (
    <div className="tag-input">
      {names.map((name) => (
        <span key={name} className="tag-chip">
          {colorFor ? (
            <span className="d" style={{ background: colorFor(name) }} />
          ) : null}
          {name}
          <button
            type="button"
            className="rm"
            aria-label={`Remove tag ${name}`}
            onClick={() => onNamesChange(names.filter((n) => n !== name))}
          >
            <Icon name="close" size={12} />
          </button>
        </span>
      ))}
      <input
        id={id}
        className="tag-input-field"
        value={draft}
        placeholder={
          names.length === 0 ? "Type a tag, press , or Enter…" : "Add another…"
        }
        onChange={(e) => {
          const v = e.target.value;
          // Commit-on-comma: every segment before the trailing one becomes
          // a chip; the tail stays as the live input.
          if (v.includes(",")) {
            const parts = v.split(",");
            const tail = parts.pop() ?? "";
            let next = names;
            for (const p of parts) next = mergePendingTag(next, p);
            if (next !== names) onNamesChange(next);
            onDraftChange(tail);
          } else {
            onDraftChange(v);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit(draft);
          } else if (e.key === "Backspace" && draft === "") {
            const last = names[names.length - 1];
            if (last) onNamesChange(names.filter((n) => n !== last));
          }
        }}
        onBlur={() => {
          if (draft.trim()) commit(draft);
        }}
      />
    </div>
  );
}
